import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * ViewModel for the sync-diagnostics surface (turn 19.1 — VM only).
 *
 * Owns a session-only ring buffer of the last 50 PostgREST / RPC fetch events
 * for the current tab. Installs a `window.fetch` interceptor on mount that
 * records every Supabase request as `{ url, method, status, durationMs,
 * startedAt }`; restores the original `window.fetch` on unmount. No
 * persistence, no Supabase round-trip from the VM itself.
 *
 * The modal overlay (19.2) and stuck-offline banner + reconnect toast (19.3)
 * land in follow-up turns. This turn ships the VM + the
 * `window.__penroseSyncDiagnostics` test seam (mirrors
 * `__penrosePersonalTodos` from 18.2b / 18.3a) so CLI specs can drive the ring
 * buffer without wiring a view.
 */

export type SyncDiagnosticsStatus = 'online' | 'degraded' | 'offline';

export interface SyncEvent {
  /** Absolute URL of the request. */
  url: string;
  /** HTTP method (uppercased). */
  method: string;
  /**
   * HTTP status code returned by the server, or `0` if `fetch` itself threw
   * (network error / CORS reject / aborted).
   */
  status: number;
  /** Wall-clock duration in milliseconds (`performance.now()` delta). */
  durationMs: number;
  /** ISO timestamp captured at the moment `fetch` was invoked. */
  startedAt: string;
}

export interface SyncDiagnosticsVMState {
  events: ReadonlyArray<SyncEvent>;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  status: SyncDiagnosticsStatus;
}

export interface SyncDiagnosticsVMActions {
  clear: () => void;
  retryNow: () => void;
}

export interface UseSyncDiagnosticsVM {
  state: SyncDiagnosticsVMState;
  actions: SyncDiagnosticsVMActions;
}

/** Capacity of the in-memory ring buffer. PRD §"Recent activity" — 50. */
export const SYNC_DIAGNOSTICS_RING_CAPACITY = 50;

/** 19.3 — stuck-offline banner threshold. After being `status === 'offline'`
 *  for this many milliseconds, the shell-level alerts surface renders the
 *  stuck-offline banner. Tests override via `window.__penroseStuckOfflineThresholdMs`
 *  to avoid having to wait 5 real minutes per spec. */
export const SYNC_DIAGNOSTICS_STUCK_OFFLINE_THRESHOLD_MS = 5 * 60 * 1000;

/** Returns the active stuck-offline threshold, honouring the test-only override
 *  on `window`. */
export function getStuckOfflineThresholdMs(): number {
  if (typeof window !== 'undefined') {
    const override = (
      window as unknown as { __penroseStuckOfflineThresholdMs?: unknown }
    ).__penroseStuckOfflineThresholdMs;
    if (typeof override === 'number' && Number.isFinite(override) && override >= 0) {
      return override;
    }
  }
  return SYNC_DIAGNOSTICS_STUCK_OFFLINE_THRESHOLD_MS;
}

/** Module-level guard so concurrent VM instances don't double-wrap `fetch`. */
let activeInstanceCount = 0;
let originalFetch: typeof window.fetch | null = null;
type FetchRecorder = (event: SyncEvent) => void;
const recorders = new Set<FetchRecorder>();
/**
 * Parallel registry of "reset" callbacks — one per live VM instance. When any
 * instance's `clear()` action fires, every instance's local state is reset,
 * so the shell's VM + the diagnostics modal's VM (which each maintain their
 * own `useState` events array) stay in lockstep. Without this, the seam's
 * `clear()` would only clear the shell VM while the modal kept rendering its
 * accumulated event list.
 */
type Clearer = () => void;
const clearers = new Set<Clearer>();

/**
 * Heuristic for "this is a Supabase request worth recording". Matches the
 * configured `VITE_SUPABASE_URL` prefix when available, plus a fallback for
 * any `*.supabase.co` host (covers Dev branch URL drift + tests that route
 * synthetic supabase.co URLs).
 */
function isSupabaseUrl(url: string): boolean {
  const configured = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (configured && url.startsWith(configured)) return true;
  return /^https?:\/\/[^/]*\.supabase\.co(\/|$)/.test(url);
}

function nowIso(): string {
  return new Date().toISOString();
}

function installFetchInterceptor(): void {
  if (originalFetch !== null) return;
  if (typeof window === 'undefined') return;
  originalFetch = window.fetch.bind(window);
  window.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (!isSupabaseUrl(url)) {
      return originalFetch!(input, init);
    }
    const startedAt = nowIso();
    const t0 = performance.now();
    try {
      const res = await originalFetch!(input, init);
      const durationMs = Math.round(performance.now() - t0);
      const event: SyncEvent = {
        url,
        method,
        status: res.status,
        durationMs,
        startedAt
      };
      for (const rec of recorders) rec(event);
      return res;
    } catch (err) {
      const durationMs = Math.round(performance.now() - t0);
      const event: SyncEvent = {
        url,
        method,
        status: 0,
        durationMs,
        startedAt
      };
      for (const rec of recorders) rec(event);
      throw err;
    }
  };
}

function uninstallFetchInterceptor(): void {
  if (originalFetch === null) return;
  if (typeof window === 'undefined') return;
  window.fetch = originalFetch;
  originalFetch = null;
}

export function useSyncDiagnosticsVM(): UseSyncDiagnosticsVM {
  const [events, setEvents] = useState<ReadonlyArray<SyncEvent>>([]);
  const [lastSuccessAt, setLastSuccessAt] = useState<string | null>(null);
  const [lastErrorAt, setLastErrorAt] = useState<string | null>(null);
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  // Track navigator.onLine so `status` reflects browser-reported connectivity.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // Subscribe to the global fetch recorder. The first mounted VM installs the
  // window.fetch wrapper; the last unmount removes it. Also register a
  // local reset so `clear()` on any sibling instance wipes this one too.
  useEffect(() => {
    const record: FetchRecorder = (event) => {
      setEvents((prev) => {
        const next = prev.length >= SYNC_DIAGNOSTICS_RING_CAPACITY
          ? prev.slice(prev.length - SYNC_DIAGNOSTICS_RING_CAPACITY + 1)
          : prev.slice();
        next.push(event);
        return next;
      });
      if (event.status >= 200 && event.status < 300) {
        setLastSuccessAt(event.startedAt);
      } else {
        setLastErrorAt(event.startedAt);
      }
    };
    const reset: Clearer = () => {
      setEvents([]);
      setLastSuccessAt(null);
      setLastErrorAt(null);
    };
    recorders.add(record);
    clearers.add(reset);
    activeInstanceCount += 1;
    if (activeInstanceCount === 1) installFetchInterceptor();
    return () => {
      recorders.delete(record);
      clearers.delete(reset);
      activeInstanceCount -= 1;
      if (activeInstanceCount === 0) uninstallFetchInterceptor();
    };
  }, []);

  const failedEventsRef = useRef<ReadonlyArray<SyncEvent>>([]);
  failedEventsRef.current = events.filter(
    (e) => !(e.status >= 200 && e.status < 300)
  );

  const clear = useCallback(() => {
    // Fan out to every live VM instance so the shell's VM + the diagnostics
    // modal's VM (which each have their own `useState` arrays) both reset.
    for (const reset of clearers) reset();
  }, []);

  const retryNow = useCallback(() => {
    // v1: re-issue a GET against every failed URL in the ring buffer. The
    // wrapper records the replay events into the same ring. The 19.2 modal
    // will wire the "Retry now" button to this action; the action itself
    // exists today so 19.2 doesn't need a VM change.
    const failed = failedEventsRef.current;
    for (const ev of failed) {
      void fetch(ev.url).catch(() => {
        // Swallow — the interceptor records the outcome either way.
      });
    }
  }, []);

  const status: SyncDiagnosticsStatus = useMemo(() => {
    if (!online) return 'offline';
    if (lastErrorAt !== null) {
      if (lastSuccessAt === null) return 'degraded';
      if (Date.parse(lastErrorAt) > Date.parse(lastSuccessAt)) return 'degraded';
    }
    return 'online';
  }, [online, lastErrorAt, lastSuccessAt]);

  const state: SyncDiagnosticsVMState = useMemo(
    () => ({ events, lastSuccessAt, lastErrorAt, status }),
    [events, lastSuccessAt, lastErrorAt, status]
  );

  const actions: SyncDiagnosticsVMActions = useMemo(
    () => ({ clear, retryNow }),
    [clear, retryNow]
  );

  // Test seam — Playwright specs reach in via `window.__penroseSyncDiagnostics`
  // to read the ring buffer + drive `clear` / `retryNow` without mounting any
  // visual surface. Mirrors the `__penrosePersonalTodos` handle from 18.2b /
  // 18.3a so spec authors recognise the shape.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handle = {
      getState: () => stateRef.current,
      clear,
      retryNow
    };
    (window as unknown as {
      __penroseSyncDiagnostics?: typeof handle;
    }).__penroseSyncDiagnostics = handle;
    return () => {
      delete (window as unknown as {
        __penroseSyncDiagnostics?: typeof handle;
      }).__penroseSyncDiagnostics;
    };
  }, [clear, retryNow]);

  return { state, actions };
}
